import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';

import didInsert from 'webapp/modifiers/did-insert';
import CommonPopupsPopupBlueBubble from 'webapp/components/common/popups/popup-blue-bubble';
import CustomQuerySetupCommonSourceSelect from 'webapp/components/custom-query/setup/common/source-select';
import SubmitButton from 'webapp/components/submit-button';

import type { CustomQuery } from 'webapp/models-custom';

interface Args {
	onSourceSelect: (model: CustomQuery) => void;
}

export default class CustomMetricsEditLandingCustomQueryComponent extends Component<Args> {
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('billing') declare billingService: Service.Billing;
	@service('tour') declare tourService: Service.Tour;
	@service('popup') declare popupService: Service.Popup;

	model: CustomQuery;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.model = this.mmCustomQueriesService.createNewModel();
	}

	get showUpgrade() {
		return !this.billingService.qbEnabled || this.notEnoughCustomMetricsLimit;
	}

	get notEnoughCustomMetricsLimit() {
		return !this.billingService.checkIfCan(BillingFeature.CUSTOM_METRICS);
	}

	@action
	upgrade() {
		if (!this.billingService.qbEnabled) {
			this.billingService.handleLimitations(BillingFeature.QB);
		} else {
			this.billingService.handleLimitations(BillingFeature.CUSTOM_METRICS);
		}
	}

	@action
	changeSource(sourceId: number): void {
		const model = this.model;
		model.sourceId = sourceId;
		model.sourceToId = sourceId;
		this.args.onSourceSelect(model);

		if (this.popupService.isOpen(CommonPopupsPopupBlueBubble)) {
			void this.popupService.findByComponent(CommonPopupsPopupBlueBubble)?.close();
		}
	}

	@action
	setupView(): void {
		later(() => {
			if (!this.showUpgrade && this.tourService.isEligibleForCustomMetricsPopupQbTour) {
				this.tourService.initTour(TourId.CUSTOM_METRICS_POPUP_QB)?.start();
			}
		}, 500);
	}

	<template>
		<div class="landing-side landing-side--custom-query" {{didInsert this.setupView}}>
			<h4>
				From a data source or dataset
				<p>Apply filters, dimensions, and segments</p>
			</h4>

			<CustomQuerySetupCommonSourceSelect
				@sourceId={{this.model.sourceId}}
				@onChange={{this.changeSource}}
			/>

			{{#if this.showUpgrade}}
				<div class="blur"></div>
				<SubmitButton @text="Upgrade" @size="big" @onTrigger={{this.upgrade}} />
			{{/if}}
		</div>
	</template>
}
